import type { HoverCardProps } from '@mantine/core';
import { Avatar, Group, HoverCard, Stack, createStyles } from '@mantine/core';
import { AIAvatar } from '@repo/common/components/AI/Avatar.tsx';
import { MONITOR_ICON_MAPPING } from '@repo/common/constants';
import { pluralize } from '@repo/common/utils';
import { getSummaryAndBreadCrumbs } from '@repo/common/utils/breadcrumb';
import { Icon, Text } from '@repo/foundations';
import { lowerCase } from 'lodash-es';
import type {
	LiteSecodaEntity,
	LiteUserOut,
} from '../../../../../packages/api-codegen';
import { type ISecodaEntity, type IUser } from '../../api';
import { getMarkdownAsPlainText } from '../../utils/getMarkdownAsPlainText';
import { type useResourceLink } from '../Link/useResourceLink';
import { SecodaEntityIcon } from '../SecodaEntity';
import type { ISecodaEntityIcon } from '../SecodaEntity/SecodaEntityIcon/SecodaEntityIcon';
import { UserAvatar } from '../UserAvatar';
import type {
	AIChatPreview,
	AIPersonaPreview,
	DefaultAIPersonaPreview,
	EntityPreview,
	MonitorPreview,
	UserPreview,
} from './ResourcePreview.types.ts';
import {
	isAIChatPreview,
	isAIPersonaPreview,
	isDefaultAIPersonaPreview,
	isEntityPreview,
	isMonitorPreview,
	isUserPreview,
} from './ResourcePreview.utils.tsx';

interface UseStylesProps {
	hasDetails: boolean;
}

const useStyles = createStyles((theme, { hasDetails }: UseStylesProps) => ({
	dropdown: {
		maxWidth: '268px',
		borderRadius: theme.radius.md,
		padding: 0,
		boxShadow: '0px 6px 16px 0px rgba(0, 0, 0, 0.12)',
		overflow: 'hidden',
	},
	title: {
		cursor: 'pointer',
		padding: hasDetails
			? `${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm}`
			: theme.spacing.sm,
		overflowWrap: 'anywhere',
	},
	body: {
		padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm}`,
	},
	description: {
		fontSize: theme.fontSizes.sm,
		display: '-webkit-box',
		lineClamp: 2,
		WebkitLineClamp: 2,
		boxOrient: 'vertical',
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
	},
	iconWrapper: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.xs,
		minWidth: theme.other.space['7'],
		height: theme.other.space['7'],
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: theme.other.space['1'],
	},
}));

interface ResourcePreviewProps
	extends Omit<ReturnType<typeof useResourceLink>, 'data'>,
		HoverCardProps {
	initiallyOpen?: boolean;
	data:
		| EntityPreview
		| UserPreview
		| MonitorPreview
		| AIChatPreview
		| AIPersonaPreview
		| LiteSecodaEntity
		| DefaultAIPersonaPreview;
}

export function ResourcePreview({
	data,
	hasError,
	isLoading,
	children,
	initiallyOpen,
	...props
}: ResourcePreviewProps) {
	const showError = hasError || (!isLoading && !data);

	let title: string;
	if (isUserPreview(data)) {
		title = data.display_name;
	} else if (isAIPersonaPreview(data)) {
		title = data.persona.name ? data.persona.name : 'New persona';
	} else if (isDefaultAIPersonaPreview(data)) {
		title = 'Secoda AI';
	} else if (isAIChatPreview(data)) {
		title = data.prompt;
	} else {
		title = data.title ?? '';
	}

	let description: string | null = null;
	if (isAIPersonaPreview(data)) {
		description = data.persona.description ?? '';
	} else if (
		!isUserPreview(data) &&
		!isAIChatPreview(data) &&
		!isDefaultAIPersonaPreview(data)
	) {
		description = data.description ?? data?.definition ?? '';
	}

	let breadcrumbs: string | null = null;
	if (isUserPreview(data)) {
		breadcrumbs = 'user';
	} else if (isAIPersonaPreview(data) || isDefaultAIPersonaPreview(data)) {
		breadcrumbs = 'ai persona';
	} else if (isAIChatPreview(data)) {
		breadcrumbs = 'ai chat';
	} else {
		breadcrumbs = getSummaryAndBreadCrumbs(
			(data as ISecodaEntity).native_type ||
				(data as ISecodaEntity).entity_type,
			isEntityPreview(data) ? data.search_metadata : {}
		);
	}

	let owners: (IUser | LiteUserOut)[] = [];

	if (isMonitorPreview(data)) {
		owners = data.owners;
	} else if (isEntityPreview(data)) {
		owners = data.display_metadata?.owners as IUser[];
	} else if (isAIChatPreview(data)) {
		owners = data.user ? [data.user] : [];
	}

	const hasOwners = owners?.length > 0;

	const { classes } = useStyles({ hasDetails: !!description || hasOwners });
	const showIcon =
		isMonitorPreview(data) || isAIChatPreview(data) || isEntityPreview(data);

	const cleanedDescription = getMarkdownAsPlainText(description ?? '', 2);
	return (
		<HoverCard
			openDelay={500}
			initiallyOpened={initiallyOpen}
			withinPortal
			{...props}
		>
			<HoverCard.Target>{children}</HoverCard.Target>
			<HoverCard.Dropdown className={classes.dropdown}>
				{isLoading && <Text className={classes.title}>Loading...</Text>}
				{showError && (
					<Text className={classes.title}>Could not load preview</Text>
				)}
				{!isLoading && !showError && (
					<Stack spacing={0}>
						<Group
							spacing="xs"
							align="flex-start"
							className={classes.title}
							noWrap
						>
							{isUserPreview(data) && <UserAvatar user={data} size="xxs" />}
							{isAIPersonaPreview(data) &&
								(data.persona.avatar_url ? (
									<Avatar
										src={data.persona.avatar_url}
										size="xxs"
										alt={`${data.persona.name} avatar`}
									/>
								) : (
									<AIAvatar size="sm" speed="none" iconSpeed="none" />
								))}
							{isDefaultAIPersonaPreview(data) && (
								<AIAvatar size="sm" speed="none" iconSpeed="none" />
							)}
							{showIcon && (
								<div className={classes.iconWrapper}>
									{isMonitorPreview(data) && (
										<Icon
											name="activity"
											color={
												MONITOR_ICON_MAPPING[data.status].iconColor ??
												MONITOR_ICON_MAPPING[data.status].iconFillColor
											}
										/>
									)}
									{isAIChatPreview(data) && <Icon name="sparkles" />}
									{isEntityPreview(data) && (
										<SecodaEntityIcon
											entity={data as ISecodaEntityIcon}
											inline
											size={20}
										/>
									)}
								</div>
							)}
							<Stack spacing={0}>
								<Text size="sm" fw="bold" color="text/primary/default">
									{title}
								</Text>
								<Text size="xs" color="text/secondary/default">
									{isAIPersonaPreview(data) ? 'AI Persona' : breadcrumbs}
								</Text>
							</Stack>
						</Group>
						{(description || hasOwners) && (
							<Stack className={classes.body}>
								{description && (
									<Text className={classes.description}>
										{cleanedDescription}
									</Text>
								)}
								{hasOwners && (
									<Group spacing="xs">
										<Avatar.Group>
											{owners.slice(0, 3).map((owner) => (
												<UserAvatar
													key={owner.id}
													user={owner as IUser}
													size="sm"
												/>
											))}
										</Avatar.Group>
										<Text size="sm" color="text/primary/default">
											{owners.length === 1
												? owners[0].display_name
												: pluralize(lowerCase('owner'), owners.length, true)}
										</Text>
									</Group>
								)}
							</Stack>
						)}
					</Stack>
				)}
			</HoverCard.Dropdown>
		</HoverCard>
	);
}
